import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Dice5, KeyRound } from 'lucide-react';
import {
  CANONICAL_ROLES,
  userCreateInputSchema,
  userUpdateInputSchema,
  type AvatarFit,
  type CanonicalRole,
  type UserCreateInput,
  type UserPublic,
  type UserUpdateInput,
} from '@underline/shared';
import { BottomSheet } from '@/components/ui/bottom-sheet';
import { FloatingField } from '@/components/ui/floating-field';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { AvatarCropEditor } from '@/components/users/avatar-crop-editor';
import {
  useCreateUser,
  useResetUserPassword,
  useSaveUserAvatarFit,
  useUpdateUser,
  useUploadUserAvatarOriginal,
} from '@/hooks/use-users';
import { useAuthStore } from '@/stores/auth-store';

interface UserFormModalProps {
  open: boolean;
  user?: UserPublic | null;
  onClose: () => void;
}

const ROLE_OPTIONS: ReadonlyArray<{ value: CanonicalRole; label: string }> = [
  { value: 'social_media', label: 'Social' },
  { value: 'gerente', label: 'Gestor' },
  { value: 'admin', label: 'Admin' },
];

export function UserFormModal({ open, user, onClose }: UserFormModalProps) {
  const me = useAuthStore((s) => s.user);
  const isAdmin = me?.role === 'admin';
  const canManage = isAdmin || me?.role === 'gerente';
  const isEdit = !!user;

  const createMut = useCreateUser();
  const updateMut = useUpdateUser();
  const resetPassword = useResetUserPassword();
  const uploadAvatarOrig = useUploadUserAvatarOriginal();
  const saveAvatarFit = useSaveUserAvatarFit();
  const [provisional, setProvisional] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorUrl, setEditorUrl] = useState<string | null>(null);
  const [editorImgW, setEditorImgW] = useState<number | null>(null);
  const [editorImgH, setEditorImgH] = useState<number | null>(null);
  const [editorInitialFit, setEditorInitialFit] = useState<AvatarFit | null>(null);

  useEffect(() => {
    if (open) setProvisional('');
  }, [open]);

  function generateProvisional() {
    const code = Math.floor(100000 + Math.random() * 900000).toString();
    setProvisional(code);
  }

  async function handleResetPassword() {
    if (!user) return;
    if (!/^\d{6}$/.test(provisional)) {
      toast.error('Use 6 dígitos numéricos');
      return;
    }
    try {
      await resetPassword.mutateAsync({ id: user.id, input: { password: provisional } });
      toast.success(`Senha provisória definida: ${provisional}`, { duration: 10000 });
      setProvisional('');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao definir senha';
      toast.error(msg);
    }
  }

  type FormShape = {
    name: string;
    email: string;
    password: string;
    role: CanonicalRole;
  };

  const form = useForm<FormShape>({
    resolver: zodResolver(
      isEdit ? (userUpdateInputSchema as never) : userCreateInputSchema,
    ) as never,
    defaultValues: {
      name: '',
      email: '',
      password: 'mudar123',
      role: 'social_media',
    },
  });

  useEffect(() => {
    if (!open) return;
    if (user) {
      form.reset({
        name: user.name,
        email: user.email,
        password: '',
        role: (CANONICAL_ROLES as readonly string[]).includes(user.role)
          ? (user.role as CanonicalRole)
          : 'social_media',
      });
    } else {
      form.reset({ name: '', email: '', password: 'mudar123', role: 'social_media' });
    }
  }, [open, user, form]);

  const role = form.watch('role');

  function originalUrl(avatarUrl: string) {
    return avatarUrl.replace('.webp', '-original.webp');
  }

  function loadImgDimensions(url: string): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => reject(new Error('Falha ao carregar imagem'));
      img.src = url;
    });
  }

  async function handleAvatarPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !user) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Selecione uma imagem');
      return;
    }
    try {
      const result = await uploadAvatarOrig.mutateAsync({ id: user.id, file });
      const origUrl = originalUrl(result.avatar_url);
      setEditorUrl(origUrl);
      setEditorImgW(result.img_w);
      setEditorImgH(result.img_h);
      setEditorInitialFit(null);
      setEditorOpen(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao enviar foto';
      toast.error(msg);
    }
  }

  async function handleAdjustAvatar() {
    if (!user?.avatar_url) return;
    const origUrl = originalUrl(user.avatar_url);
    try {
      const { width, height } = await loadImgDimensions(origUrl);
      setEditorUrl(origUrl);
      setEditorImgW(width);
      setEditorImgH(height);
      setEditorInitialFit(null);
      setEditorOpen(true);
    } catch {
      toast.error('Não foi possível carregar a imagem original para reposicionar');
    }
  }

  async function handleAvatarFitConfirm(fit: AvatarFit) {
    if (!user) return;
    try {
      await saveAvatarFit.mutateAsync({ id: user.id, fit });
      toast.success('Foto reposicionada');
      setEditorOpen(false);
      setEditorUrl(null);
      setEditorImgW(null);
      setEditorImgH(null);
      setEditorInitialFit(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao reposicionar';
      toast.error(msg);
    }
  }

  async function onSubmit(data: FormShape) {
    try {
      if (isEdit && user) {
        const patch: UserUpdateInput = {
          name: data.name,
          email: data.email,
        };
        if (isAdmin) patch.role = data.role;
        await updateMut.mutateAsync({ id: user.id, input: patch });
        toast.success('Usuário atualizado');
      } else {
        const payload: UserCreateInput = {
          name: data.name,
          username: (data.email.split('@')[0] ?? data.email).toLowerCase().replace(/[^a-z0-9_]/g, '_'),
          email: data.email,
          password: data.password,
          role: isAdmin ? data.role : 'social_media',
        };
        await createMut.mutateAsync(payload);
        toast.success('Usuário criado');
      }
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao salvar';
      toast.error(msg);
    }
  }

  return (
    <BottomSheet open={open} onClose={onClose} title={isEdit ? 'Editar usuário' : 'Novo usuário'}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        {isEdit && canManage && user && (
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 shrink-0 rounded-full overflow-hidden border border-border bg-muted grid place-items-center text-muted-foreground">
              {user.avatar_url ? (
                <img src={user.avatar_url} alt={user.name} className="h-full w-full object-cover" />
              ) : (
                <span className="mono text-[10px] uppercase">sem foto</span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <label className="cursor-pointer rounded-lg border border-border bg-card px-3 py-2 text-xs font-medium hover:bg-muted">
                {uploadAvatarOrig.isPending ? 'Enviando...' : 'Trocar foto'}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleAvatarPick}
                  disabled={uploadAvatarOrig.isPending}
                />
              </label>
              {user.avatar_url && (
                <button
                  type="button"
                  onClick={handleAdjustAvatar}
                  className="rounded-lg border border-border bg-card px-3 py-2 text-xs font-medium hover:bg-muted"
                >
                  Reposicionar
                </button>
              )}
            </div>
          </div>
        )}

        <FloatingField
          label="Nome"
          error={form.formState.errors.name?.message}
          {...form.register('name')}
        />
        <FloatingField
          label="E-mail"
          type="email"
          autoComplete="off"
          error={form.formState.errors.email?.message}
          {...form.register('email')}
        />
        {!isEdit && (
          <FloatingField
            label="Senha inicial"
            type="text"
            autoComplete="off"
            error={form.formState.errors.password?.message}
            {...form.register('password')}
          />
        )}
        {isAdmin && (
          <div>
            <p className="eyebrow mb-2">Papel</p>
            <SegmentedControl
              options={ROLE_OPTIONS}
              value={role}
              onChange={(v) => form.setValue('role', v, { shouldDirty: true })}
              size="sm"
            />
          </div>
        )}

        {isEdit && canManage && user && user.id !== me?.id && (
          <div className="rounded-lg border border-dashed border-border p-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="eyebrow flex items-center gap-1.5">
                <KeyRound className="h-3 w-3" />
                Senha provisória
              </p>
              <button
                type="button"
                onClick={generateProvisional}
                className="text-xs font-semibold text-primary inline-flex items-center gap-1"
              >
                <Dice5 className="h-3 w-3" /> Gerar
              </button>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Define 6 dígitos numéricos e força o usuário a cadastrar uma nova senha no
              próximo login.
            </p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                inputMode="numeric"
                autoComplete="off"
                maxLength={6}
                pattern="\d{6}"
                value={provisional}
                onChange={(e) => setProvisional(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="w-32 shrink-0 rounded-lg border border-border bg-card px-2 py-2 mono text-sm tracking-[0.25em] tabular-nums text-center outline-none focus:border-foreground transition-colors"
              />
              <button
                type="button"
                onClick={handleResetPassword}
                disabled={resetPassword.isPending || provisional.length !== 6}
                className="flex-1 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground shadow-red disabled:opacity-60"
              >
                {resetPassword.isPending ? 'Definindo...' : 'Definir'}
              </button>
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border border-border bg-card px-4 py-3 text-sm font-medium hover:bg-muted"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={createMut.isPending || updateMut.isPending}
            className="flex-[2] rounded-lg bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground shadow-red disabled:opacity-60"
          >
            {createMut.isPending || updateMut.isPending
              ? 'Salvando...'
              : isEdit
                ? 'Salvar'
                : 'Criar usuário'}
          </button>
        </div>
      </form>

      <AvatarCropEditor
        open={editorOpen}
        imageUrl={editorUrl}
        imgW={editorImgW}
        imgH={editorImgH}
        initialFit={editorInitialFit}
        isSaving={saveAvatarFit.isPending}
        onClose={() => {
          setEditorOpen(false);
          setEditorUrl(null);
          setEditorImgW(null);
          setEditorImgH(null);
          setEditorInitialFit(null);
        }}
        onConfirm={handleAvatarFitConfirm}
      />
    </BottomSheet>
  );
}
